import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { money, qty, date, unitPrice, useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import { InvoiceStatus } from './DashboardPage';
import type { InvoiceDetail } from '../types';

/** Карточка счёта: из чего он собрался, что по нему пришло, что с ним можно сделать. */
export function InvoicePage() {
  const { tenantId = '', invoiceId = '' } = useParams();
  const queryClient = useQueryClient();
  const catalog = useTenantCatalog(tenantId);
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [payLink, setPayLink] = useState<string | null>(null);

  const invoice = useQuery({
    queryKey: ['invoice', tenantId, invoiceId],
    queryFn: () => api.get<InvoiceDetail>(`/tenants/${tenantId}/invoices/${invoiceId}`),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['invoice', tenantId, invoiceId] });
    queryClient.invalidateQueries({ queryKey: ['invoices', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['ledger', tenantId] });
  };

  const pay = useMutation({
    mutationFn: () =>
      api.post(`/tenants/${tenantId}/invoices/${invoiceId}/payments`, {
        amountKopecks: Math.round(Number(amount) * 100),
        note: note || undefined,
      }),
    onSuccess: () => {
      refresh();
      setPaying(false);
      setAmount('');
      setNote('');
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const online = useMutation({
    mutationFn: () =>
      api.post<{ confirmationUrl: string | null }>(`/tenants/${tenantId}/invoices/${invoiceId}/pay`),
    onSuccess: (payment) => setPayLink(payment.confirmationUrl),
    onError: (err) => setError((err as ApiError).message),
  });

  const voidInvoice = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/invoices/${invoiceId}/void`),
    onSuccess: refresh,
    onError: (err) => setError((err as ApiError).message),
  });

  if (invoice.isLoading) return <div className="state">Загрузка…</div>;
  if (invoice.isError) return <div className="state">Счёт не найден</div>;

  const { invoice: head, lines, payments } = invoice.data!;
  const remaining = head.totalKopecks - head.paidKopecks;
  const customer = catalog.customerById.get(head.customerId);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="muted small">
            <Link to={`/t/${tenantId}/invoices`}>Счета</Link>
          </div>
          <h1>Счёт {head.number}</h1>
          <div className="muted small">
            {customer?.name ?? '—'} · период {date(head.periodStart)} — {date(head.periodEnd)} · оплатить
            до {date(head.dueDate)}
          </div>
        </div>
        <div className="spacer" />
        <InvoiceStatus status={head.status} />
      </div>

      <div className="cards-row">
        <section className="card grow">
          <div className="card-head">
            <h2>Позиции</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num">Количество</th>
                <th className="num">Цена</th>
                <th className="num">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td>{line.description}</td>
                  <td className="num">{qty(line.quantity)}</td>
                  <td className="num">{unitPrice(line)}</td>
                  <td className="num">{money(line.amountKopecks)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={3}>Итого</th>
                <th className="num">{money(head.totalKopecks)} ₽</th>
              </tr>
            </tfoot>
          </table>
        </section>

        <section className="card">
          <div className="card-head">
            <h2>Оплата</h2>
          </div>
          <ul className="facts">
            <li>
              <span>Начислено</span>
              <b>{money(head.totalKopecks)} ₽</b>
            </li>
            <li>
              <span>Оплачено</span>
              <b>{money(head.paidKopecks)} ₽</b>
            </li>
            <li>
              <span>Остаток</span>
              <b>{money(remaining)} ₽</b>
            </li>
          </ul>

          {head.status !== 'VOID' && remaining > 0 && (
            <div className="stack">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setError(null);
                  setAmount((remaining / 100).toFixed(2));
                  setPaying(true);
                }}
              >
                Отметить оплату
              </button>
              <button
                className="btn btn-secondary"
                disabled={online.isPending}
                onClick={() => { setError(null); online.mutate(); }}
              >
                <Icon name="link" />
                Ссылка на онлайн-оплату
              </button>
              {payLink && (
                <a className="paylink" href={payLink} target="_blank" rel="noreferrer">
                  Открыть платёжную страницу
                </a>
              )}
            </div>
          )}

          {head.shortLink && (
            <div className="form-hint">
              Короткая ссылка для абонента: <code>{head.shortLink}</code>
            </div>
          )}

          {head.status === 'ISSUED' && head.paidKopecks === 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setError(null); voidInvoice.mutate(); }}
            >
              Аннулировать счёт
            </button>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="card-head" style={{ marginTop: '1rem' }}>
            <h2>Поступления</h2>
          </div>
          {payments.length === 0 ? (
            <div className="state">Оплат ещё не было</div>
          ) : (
            <ul className="facts">
              {payments.map((p) => (
                <li key={p.id}>
                  <span>
                    {date(p.paidAt)}
                    {p.note ? ` · ${p.note}` : ''}
                  </span>
                  <b>{money(p.amountKopecks)} ₽</b>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {paying && (
        <Modal title="Отметить оплату" onClose={() => setPaying(false)}>
          <div className="form-group">
            <label className="form-label" htmlFor="pay-amount">
              Сумма, ₽
            </label>
            <input
              id="pay-amount"
              className="input"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
            <div className="form-hint">Больше остатка провести нельзя.</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="pay-note">
              Примечание
            </label>
            <input
              id="pay-note"
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="платёжное поручение №…"
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setPaying(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!amount || pay.isPending}
              onClick={() => { setError(null); pay.mutate(); }}
            >
              Провести
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
